/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import ParticleEffect from '../Effects/ParticleEffect';
import Currency, { CurrencyType } from '../Localization/Currency';
import BetManager from '../Playbar/BetManager';
import PlayResultComponent from '../Server/PlayResultComponent';
import StateDependencyComponent from '../StateManager/StateDependencyComponent';
import StateEvent from '../StateManager/StateEvent';
import { StateID } from '../StateManager/StateID';
import StateReference from '../StateManager/StateReference';
import IAnimate from '../Utility/Component/Animation/IAnimate';
import InjectableComponent from '../Utility/Component/ComponentReference/InjectableComponent';
import NodeCollection from '../Utility/Component/ComponentReference/NodeCollection';
import { ReferenceGroup } from '../Utility/Component/ComponentReference/ReferenceGroup';
import CLog, { LogTypes } from '../Utility/ComponentLogger';
import LogoAnimDefinition from './LogoAnimDefinition';
import RecoveryManager from './RecoveryManager';
import WinCelebration from './WinCelebration';
import { IPlayState } from '../Server/Object/PlayResult';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

/**
 * Manager responsible for sorting and triggering win celebrations.
 */
@cc._decorator.ccclass
export default class WinCelebrationManager extends StateDependencyComponent
{
    @property({ type: StateReference, visible: true })
    protected _initState: StateReference = new StateReference(StateID.CheckRecall, false);

    /** @hidden */
    @property({ type: [StateReference], visible: true })
    protected _primaryStates: StateReference[] = [new StateReference(StateID.Award, true)];
    /** The states on which to check if the current award reaches any celebration thresholds. */
    public get PrimaryStates(): StateReference[] { return this._primaryStates; }

    /** The state on which to clear the coin spray particle effect. */
    @property(StateReference)
    protected clearParticleState: StateReference = new StateReference(StateID.PostPlay, true);

    /** Reference to logo anim to animate on win celebrations. */
    @property({ type: InjectableComponent })
    protected logoNodes: InjectableComponent<NodeCollection>
        = new InjectableComponent(ReferenceGroup.MainGame, 'Game.Logo');

    private _logoAnimations: IAnimate[] = [];

    @property({ visible: true })
    protected _useSingleSpinAward: boolean = false;

    /** @hidden */
    private _playResultRef: InjectableComponent<PlayResultComponent> = new InjectableComponent();
    /** @hidden */
    private _betManagerRef: InjectableComponent<BetManager> = new InjectableComponent();
    /** @hidden */
    private _recoveryManagerRef: InjectableComponent<RecoveryManager> = new InjectableComponent();
    /** @hidden */
    private _winCelebrations: WinCelebration[] = [];
    /** @hidden */
    private _activatedCelebration: WinCelebration = null;

    //#region Component Events

    /** @hidden */
    protected setContexts(): void
    {
        this.logoNodes.SetContext(NodeCollection, { InjectOnce: true });
        this._betManagerRef.SetContext(BetManager);
        this._playResultRef.SetContext(PlayResultComponent);
        this._recoveryManagerRef.SetContext(RecoveryManager);
    }

    /** @hidden */
    protected onLoad(): void
    {
        super.onLoad();

        this._winCelebrations = this.node.getComponentsInChildren(WinCelebration);

        // Lets sort the win levels decreasing in value to make score handling easier.
        this._winCelebrations = this._winCelebrations.sort((a, b) => b.ThresholdMultiplier - a.ThresholdMultiplier);
    }

    /** @hidden */
    protected start(): void
    {
        super.start(this._primaryStates, this.clearParticleState, this._initState);

        for (let winCelebration of this._winCelebrations)
        {
            winCelebration.EffectManager.OnComplete(() => this.OnCelebrationComplete());
        }

        CLog.log(`Started with [${this._winCelebrations.length}] win celebratons.`, LogTypes.Initialization, this);
    }

    //#endregion

    //#region Handlers

    /**
     * Handler called when a win celebration has completed.
     */
    public OnCelebrationComplete = (): void =>
    {
        this.deactivate();
    }

    /** @hidden */
    protected onStateChangeHandler = (event: StateEvent): void =>
    {
        if (this._initState.Value === event.State && this.logoNodes.InstanceID)
        {
            this.logoNodes.WithValue(nodeCollection =>
                {
                    nodeCollection.Nodes.forEach(node =>
                        {
                            const logoDef: LogoAnimDefinition = node.getComponent(LogoAnimDefinition);
                            if (!logoDef) { return; }
                            this._logoAnimations.push(logoDef.GetAnimationWrapper());
                        });
                });
        }

        if (!this._winCelebrations || this._winCelebrations.length === 0) { return; }

        if (this._primaryStates.some((x) => x.Value === event.State))
        {
            if (this._recoveryManagerRef.Value.Recovering)
            {
                this.completeState(event.State);
                return;
            }

            let playState: IPlayState = this._playResultRef.Value.PlayState;

            let prizeAmount: number = 0;
            let awardValue: Currency = null;

            if (this._useSingleSpinAward)
            {
                prizeAmount = this._playResultRef.Value.GetCurrentGameResult().totalPrize;
                awardValue = new Currency(this._betManagerRef.Value.ActiveDenomObservable, prizeAmount,
                    CurrencyType.CREDITS);
            }
            else
            {
                prizeAmount = playState.winMc;
                awardValue = new Currency(this._betManagerRef.Value.ActiveDenomObservable, prizeAmount,
                    CurrencyType.MC);
            }

            let currentBet: Currency = this._betManagerRef.Value.CurrentBet;

            if (awardValue.GetValue() > 0)
            {
                // Relies on sorted list, seems repetitive to sort each time.
                for (let winCelebration of this._winCelebrations)
                {
                    // This is not the celebraton we are looking for..
                    if (awardValue.GetValue() < winCelebration.GetWinLevelMC(currentBet)) { continue; }

                    this.activate(winCelebration);
                    return;
                }
            }

            this.completeState(event.State);
        }
        else if (event.State === this.clearParticleState.Value)
        {
            for (let particleEffect of this.getComponentsInChildren(ParticleEffect))
            {
                particleEffect.StopSystem();
            }

            if (this._logoAnimations.length > 0)
            {
                this._logoAnimations.forEach(anim => anim.Stop());
            }

            this.completeState(event.State);
        }
    }

    //#endregion

    //#region Local Helpers

    /**
     * Triggers the indicated [[WinCelebration]].
     * @param winCelebration The celebration to be triggered.
     */
    private activate(winCelebration: WinCelebration): void
    {
        if (this._primaryStates.some(StateReference.IsCurrentFilter))
        {
            this._activatedCelebration = winCelebration;
            winCelebration.Trigger();
            if (this._logoAnimations.length > 0)
            {
                this._logoAnimations.forEach(anim => anim.Play());
            }
        }
    }

    /**
     * Stops the current win celebration.
     */
    private deactivate(): void
    {
        this._primaryStates.forEach(stateRef => stateRef.Value !== null
            && this.completeState(stateRef.Value), this);

        if (this._activatedCelebration)
        {
            this._activatedCelebration.Stop();
            this._activatedCelebration = null;
        }
    }

    //#endregion
}
